/**
 * LogoUploadPanel — officer/admin-facing widget to upload / replace
 * the association logo. Powers the "Manage Union" surface.
 *
 * Renders three states:
 *   1. Current logo tile (or monogram fallback)
 *   2. File picker + drag-and-drop zone
 *   3. Upload progress + result / error
 *
 * Endpoint used:
 *   POST   /api/associations/{id}/logo   (multipart file)
 *   DELETE /api/associations/{id}/logo   (clear override)
 *
 * The parent component owns the assoc object and calls `onSaved()`
 * after a successful upload so it can re-fetch fresh card data.
 */
import { useRef, useState } from "react";
import api from "@/lib/api";
import UnionLogo from "@/components/UnionLogo";
import { Upload, Trash2, ImageOff, CheckCircle2 } from "lucide-react";

const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";
const MAX_BYTES = 2 * 1024 * 1024;

export default function LogoUploadPanel({ assoc, onSaved }) {
  const inputRef = useRef(null);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");
  const [okMsg, setOkMsg]   = useState("");
  const [dragging, setDragging] = useState(false);

  async function handleFile(file) {
    setError("");
    setOkMsg("");
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) {
      setError("Use a PNG, JPG, WebP or SVG.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("File is larger than 2 MB.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/associations/${assoc.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const bytes = data.size || file.size || 0;
      const shown = bytes < 1024 ? `${bytes} B` : `${Math.round(bytes / 1024)} KB`;
      setOkMsg(`Logo uploaded (${shown}).`);
      onSaved && onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function handleClear() {
    setError("");
    setOkMsg("");
    setBusy(true);
    try {
      await api.delete(`/associations/${assoc.id}/logo`);
      setOkMsg("Logo cleared. Members will see the monogram tile until you upload again.");
      onSaved && onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't clear the logo.");
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    if (busy) return;
    handleFile(e.dataTransfer.files?.[0]);
  }

  const hasLogo = !!assoc?.logo_url;

  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-6"
         data-testid="logo-upload-panel">
      <div className="flex items-center gap-2 mb-4">
        <ImageOff size={14} className="text-[#F5B82E]" />
        <h3 className="text-[11px] uppercase tracking-[0.3em] text-white/70 font-semibold">
          Union Logo
        </h3>
      </div>

      <div className="flex items-start gap-6 flex-wrap">
        {/* Preview */}
        <div className="flex flex-col items-center gap-2 shrink-0">
          <UnionLogo assoc={assoc} size={96} rounded="xl" className="shadow-lg" />
          <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">
            {hasLogo ? "Current logo" : "Monogram fallback"}
          </div>
        </div>

        {/* Uploader */}
        <div className="flex-1 min-w-[240px]">
          <label
            htmlFor={`logo-input-${assoc.id}`}
            onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={
              "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 cursor-pointer transition-colors " +
              (dragging
                ? "border-[#F5B82E] bg-[#F5B82E]/5"
                : "border-white/15 hover:border-white/30 bg-black/20")
            }
            data-testid="logo-upload-dropzone">
            <Upload size={22} className="text-[#F5B82E]" />
            <div className="text-sm text-white/85 font-medium">
              {busy ? "Uploading…" : "Drop a logo or click to browse"}
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
              PNG · JPG · WebP · SVG · Max 2 MB
            </div>
            <input
              ref={inputRef}
              id={`logo-input-${assoc.id}`}
              type="file"
              accept={ACCEPT}
              className="sr-only"
              onChange={(e) => handleFile(e.target.files?.[0])}
              disabled={busy}
              data-testid="logo-upload-input"
            />
          </label>

          {/* Actions row */}
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="text-[10px] uppercase tracking-[0.24em] text-white/85 hover:text-[#F5B82E] disabled:opacity-40"
              data-testid="logo-upload-browse">
              Browse files
            </button>
            {hasLogo && (
              <button
                type="button"
                onClick={handleClear}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-white/50 hover:text-red-400 disabled:opacity-40"
                data-testid="logo-upload-clear">
                <Trash2 size={11} /> Clear logo
              </button>
            )}
          </div>

          {/* Feedback */}
          {error && (
            <div className="mt-3 text-[11px] text-red-400" data-testid="logo-upload-error">
              {error}
            </div>
          )}
          {okMsg && (
            <div className="mt-3 flex items-center gap-1.5 text-[11px] text-emerald-400"
                 data-testid="logo-upload-ok">
              <CheckCircle2 size={12} /> {okMsg}
            </div>
          )}
        </div>
      </div>

      <p className="mt-6 text-[11px] text-white/45 leading-relaxed max-w-xl">
        Uploading here overrides any existing default logo. Every card,
        featured strip and profile hero updates instantly for every
        visitor of Call Sheet.
      </p>
    </div>
  );
}
